import { useState } from "react";
import { Plus, Trash2, Zap, ArrowLeft, Eye, Send, Save } from "lucide-react";
import { toast } from "sonner";
import { useResource } from "../lib/hooks";
import { api, errMsg } from "../lib/api";
import { PageHeader, Pill } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Canvas } from "../components/workflows/Canvas";
import { SidePanel } from "../components/workflows/SidePanel";
import { RunLogs, RunSteps } from "../components/workflows/RunLogs";
import { normalise, findT } from "../components/workflows/catalog";

function ifFields(steps) {
  return (steps || []).flatMap((s) => (s.type === "if_else" ? [s.config?.field, ...ifFields(s.yes), ...ifFields(s.no)] : [])).filter(Boolean);
}

function TestDialog({ wid, steps, onClose }) {
  const extra = [...new Set(ifFields(steps))].filter((f) => !["first_name", "last_name", "email", "phone"].includes(f));
  const [s, setS] = useState({ first_name: "Sam", last_name: "Sample", email: "", phone: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (live) => {
    if (live && !window.confirm("This really sends the emails and texts in this workflow to the people it's set up for. Continue?")) return;
    setBusy(true);
    try { setRes({ ...(await api.post(`/workflows/${wid}/test`, { sample: s, live })).data, live }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wf-test-dialog">
        <DialogHeader><DialogTitle>Preview workflow</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground"><b>Preview</b> shows what each step would do with this sample contact and sends nothing. <b>Send real test</b> actually sends the emails and texts so you can check they arrive; tags and tasks are still only previewed.</p>
        <div className="grid grid-cols-2 gap-3">{[["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["phone", "Mobile"]].map(([k, l]) => (
          <div key={k} className="space-y-1"><Label>{l}</Label><Input value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`wf-test-${k}`} /></div>))}</div>
        {extra.length > 0 && <div className="space-y-2 rounded-md border p-3"><div className="text-xs text-muted-foreground">Sample answers for your If / else checks</div>
          {extra.map((f, i) => <div key={f} className="space-y-1"><Label>{f}</Label><Input value={s[f] || ""} onChange={(e) => setS({ ...s, [f]: e.target.value })} data-testid={`wf-test-extra-${i}`} /></div>)}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="secondary" disabled={busy} onClick={() => run(false)} data-testid="wf-test-run"><Eye className="mr-1 h-4 w-4" />Preview</Button>
          <Button disabled={busy} onClick={() => run(true)} data-testid="wf-test-live"><Send className="mr-1 h-4 w-4" />{busy ? "Sending…" : "Send real test"}</Button>
        </div>
        {res && <div data-testid="wf-test-result"><div className="mb-1 text-xs text-muted-foreground">{res.live ? "Real test sent:" : "Preview only, nothing sent:"}</div><RunSteps steps={res.steps} />{!res.steps.length && <p className="text-sm text-muted-foreground">This workflow has no actions yet.</p>}</div>}
      </DialogContent>
    </Dialog>
  );
}

function Builder({ wf, forms, onSave, onBack }) {
  const [w, setW] = useState(() => normalise(wf));
  const [panel, setPanel] = useState(null);
  const [dirty, setDirty] = useState(!wf.id);
  const [test, setTest] = useState(false);
  const update = (p) => { setW((x) => ({ ...x, ...p })); setDirty(true); };
  const save = async (extra = {}) => {
    const next = { ...w, ...extra };
    const saved = await onSave({ ...next, trigger: null, trigger_ref: null, actions: [] });
    setW(normalise({ ...next, ...saved })); setDirty(false);
    return saved;
  };
  const publish = async (on) => {
    if (on && (!w.triggers.length || !w.steps.length)) return toast.error("Add a trigger and at least one action before publishing");
    await save({ active: on }); toast.success(on ? "Workflow published" : "Workflow set to draft");
  };
  const startTest = async () => { if (dirty || !w.id) await save(); setTest(true); };
  return (
    <div data-testid="workflow-builder">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="wf-back"><ArrowLeft className="mr-1 h-4 w-4" />Workflows</Button>
        <Input className="h-10 max-w-sm border-transparent bg-transparent font-display text-xl hover:border-input" value={w.name || ""} onChange={(e) => update({ name: e.target.value })} data-testid="workflow-name-input" />
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={startTest} data-testid="wf-test-btn"><Eye className="mr-1 h-4 w-4" />Preview workflow</Button>
          <label className="flex items-center gap-2 text-sm">Draft<Switch checked={!!w.active} onCheckedChange={publish} data-testid="workflow-active-switch" />Publish</label>
          <Button onClick={async () => { await save(); toast.success("Workflow saved"); }} disabled={!dirty} data-testid="save-workflow-btn"><Save className="mr-1 h-4 w-4" />{dirty ? "Save" : "Saved"}</Button>
        </div>
      </div>
      <Tabs defaultValue="builder">
        <TabsList className="mb-4">
          <TabsTrigger value="builder" data-testid="wf-tab-builder">Builder</TabsTrigger>
          <TabsTrigger value="settings" data-testid="wf-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="logs" disabled={!w.id} data-testid="wf-tab-logs">Execution logs</TabsTrigger>
        </TabsList>
        <TabsContent value="builder"><Canvas w={w} forms={forms} panel={panel} setPanel={setPanel} update={update} /></TabsContent>
        <TabsContent value="settings"><div className="max-w-xl space-y-4 rounded-md border bg-card p-6" data-testid="wf-settings">
          <div className="space-y-1.5"><Label>Workflow name</Label><Input value={w.name || ""} onChange={(e) => update({ name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={w.description || ""} onChange={(e) => update({ description: e.target.value })} data-testid="wf-description" /></div>
          <p className="text-xs text-muted-foreground">Workflows only run when published. Draft workflows can still be previewed.</p>
        </div></TabsContent>
        {w.id && <TabsContent value="logs"><RunLogs key={test ? 1 : 0} wid={w.id} /></TabsContent>}
      </Tabs>
      <SidePanel w={w} panel={panel} setPanel={setPanel} update={update} forms={forms} />
      {test && <TestDialog wid={w.id} steps={w.steps} onClose={() => setTest(false)} />}
    </div>
  );
}

export default function Workflows() {
  const wfs = useResource("workflows");
  const forms = useResource("forms");
  const [edit, setEdit] = useState(null);
  if (edit) return <Builder key={edit.id || "new"} wf={edit} forms={forms.items} onBack={() => setEdit(null)}
    onSave={async (w) => { try { const s = await wfs.save(w, w.id); return s; } catch (e) { toast.error(errMsg(e)); throw e; } }} />;
  return (
    <div>
      <PageHeader overline="Automation" title="Workflows" desc="When something happens, the right people hear about it automatically.">
        <Button onClick={() => setEdit({ name: "New workflow", active: false, triggers: [], steps: [] })} data-testid="new-workflow-btn"><Plus className="mr-1 h-4 w-4" />New workflow</Button>
      </PageHeader>
      <div className="rounded-md border border-white/[0.06]">
        <div className="hidden sm:grid grid-cols-[20px_1fr_220px_90px_120px] gap-3 border-b border-white/[0.06] px-3 py-1.5 text-[11px] text-slate-500"><span /><span>Name</span><span>Trigger</span><span>Status</span><span /></div>
        {!wfs.items.length && <div className="px-3 py-4 text-center text-xs text-muted-foreground">No workflows yet. Create one to send alerts when a form or referral comes in.</div>}
        {wfs.items.map((raw) => { const w = normalise(raw); return (
          <div key={w.id} className="group grid grid-cols-[20px_1fr_auto] sm:grid-cols-[20px_1fr_220px_90px_120px] items-center gap-3 px-3 py-1.5 hover:bg-[#C2A462]/[0.08]" data-testid={`workflow-card-${w.id}`}>
            <Zap className="h-4 w-4 text-[#C2A462]" />
            <button className="min-w-0 truncate text-left text-[13px]" onClick={() => setEdit(raw)}>{w.name}<span className="ml-2 text-[11px] text-muted-foreground">{w.steps.length} action{w.steps.length === 1 ? "" : "s"}</span></button>
            <span className="hidden truncate text-xs text-muted-foreground sm:block">{w.triggers.map((t) => findT(t.type).label).join(" or ") || "No trigger"}</span>
            <span className={`hidden text-xs sm:block ${w.active ? "text-emerald-400" : "text-slate-500"}`}>{w.active ? "Published" : "Draft"}</span>
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEdit(raw)} data-testid={`edit-workflow-${w.id}`}>Open</Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => window.confirm("Delete this workflow?") && wfs.remove(w.id)} data-testid={`delete-workflow-${w.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
            </div>
          </div>); })}
      </div>
    </div>
  );
}
